import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { allowanceRows } from '../lib/aiAllowance'

// AI Activity: usage and cost per month / user / day, and the audit of every approved AI change.
const monthKey = d => d.toISOString().slice(0, 7)
const money = v => '$' + (Number(v) || 0).toFixed(2)
const nice = v => (Number(v) || 0).toLocaleString('en-GB')
const TONE = { red: '#C93B47', amber: '#8a5c0a', green: '#137050', none: 'var(--muted)' }

export default function AIActivity({ clientId } = {}) {
  const { config } = useAuth()
  const isAdmin = config?.accountType === 'admin' || !!config?.is_admin
  const now = new Date()
  const months = [0, 1, 2].map(k => monthKey(new Date(now.getFullYear(), now.getMonth() - k, 15)))
  const [month, setMonth] = useState(months[0])
  const [usage, setUsage] = useState([])
  const [audit, setAudit] = useState([])
  const [names, setNames] = useState({})
  const [loading, setLoading] = useState(true)
  const [vessels, setVessels] = useState({})
  const [limits, setLimits] = useState({})
  const [vessel, setVessel] = useState(clientId || 'all')
  const [err, setErr] = useState('')

  useEffect(() => {
    if (!config?.id) return
    let live = true
    setLoading(true); setErr('')
    const from = month + '-01T00:00:00Z'
    const [y, m] = month.split('-').map(Number)
    const to = new Date(Date.UTC(y, m, 1)).toISOString()
    Promise.all([
      (vessel === 'all' ? supabase.from('ai_usage').select('client_id,created_at,user_id,user_label,model,kind,calls,input_tokens,output_tokens,cache_read,cache_write,cost_usd') : supabase.from('ai_usage').select('client_id,created_at,user_id,user_label,model,kind,calls,input_tokens,output_tokens,cache_read,cache_write,cost_usd').eq('client_id', vessel)).gte('created_at', from).lt('created_at', to).order('created_at', { ascending: false }).limit(10000),
      (vessel === 'all' ? supabase.from('ai_audit').select('client_id,created_at,user_id,tool,ok,error,changes') : supabase.from('ai_audit').select('client_id,created_at,user_id,tool,ok,error,changes').eq('client_id', vessel)).gte('created_at', from).lt('created_at', to).order('created_at', { ascending: false }).limit(500),
      supabase.from('crew_accounts').select('user_id,name'),
      // the AI limit column only once sql/ai-budget.sql has run: without it, the names as before
      supabase.from('clients').select('id,vessel_name,ai_monthly_limit_usd').then(r => r.error ? supabase.from('clients').select('id,vessel_name') : r),
    ]).then(([u, a, c, v]) => {
      setVessels(Object.fromEntries((v.data || []).map(x => [x.id, x.vessel_name])))
      setLimits(Object.fromEntries((v.data || []).map(x => [x.id, x.ai_monthly_limit_usd ?? null])))
      if (!live) return
      if (u.error && a.error) setErr('Could not load AI activity: ' + (u.error.message || a.error.message))
      setUsage(u.data || []); setAudit(a.data || [])
      setNames(Object.fromEntries((c.data || []).filter(x => x.user_id).map(x => [x.user_id, x.name])))
      setLoading(false)
    })
    return () => { live = false }
  }, [config?.id, month, vessel])

  const who = (id, label) => names[id] || label || 'Captain / owner'
  const tot = usage.reduce((t, r) => ({ q: t.q + 1, cost: t.cost + (Number(r.cost_usd) || 0), tin: t.tin + (r.input_tokens || 0), tout: t.tout + (r.output_tokens || 0), cr: t.cr + (r.cache_read || 0) }), { q: 0, cost: 0, tin: 0, tout: 0, cr: 0 })
  const byUser = Object.values(usage.reduce((m, r) => { const k = r.user_id || 'x'; m[k] = m[k] || { name: who(r.user_id, r.user_label), q: 0, cost: 0 }; m[k].q++; m[k].cost += Number(r.cost_usd) || 0; return m }, {})).sort((a, b) => b.q - a.q)
  const byVessel = Object.values(usage.reduce((m, r) => { const k = r.client_id || 'x'; m[k] = m[k] || { name: vessels[k] || 'Unknown vessel', q: 0, cost: 0 }; m[k].q++; m[k].cost += Number(r.cost_usd) || 0; return m }, {})).sort((a, b) => b.q - a.q)
  const byDay = usage.reduce((m, r) => { const d = String(r.created_at).slice(0, 10); m[d] = (m[d] || 0) + 1; return m }, {})
  const days = Object.keys(byDay).sort(), maxDay = Math.max(1, ...Object.values(byDay))
  const saved = tot.cr ? Math.round(100 * tot.cr / (tot.cr + tot.tin)) : 0
  const allowance = isAdmin ? allowanceRows(usage, vessels, limits, vessel === 'all' ? null : vessel) : []

  const card = { background: 'var(--hull)', border: '0.5px solid var(--border)', borderRadius: 12, padding: 16 }
  const lab = { fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)' }

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {months.map(mk => (
          <button key={mk} onClick={() => setMonth(mk)} style={{ padding: '6px 14px', borderRadius: 16, border: '1px solid ' + (mk === month ? 'var(--gold)' : 'var(--border)'), background: mk === month ? 'rgba(176,138,46,.12)' : 'transparent', color: 'var(--text)', fontWeight: mk === month ? 700 : 400, cursor: 'pointer', fontSize: 12.5 }}>
            {new Date(mk + '-15').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
          </button>
        ))}
        {!clientId && <select value={vessel} onChange={e => setVessel(e.target.value)} style={{ marginLeft: 8, padding: '6px 10px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'var(--hull)', color: 'var(--text)', fontSize: 12.5 }}>
          <option value="all">All vessels</option>
          {Object.entries(vessels).sort((a, b) => String(a[1]).localeCompare(String(b[1]))).map(([id, n]) => <option key={id} value={id}>{n}</option>)}
        </select>}
        {loading && <span style={{ fontSize: 12, color: 'var(--muted)' }}>Loading&hellip;</span>}
      </div>
      {err && <div style={{ color: '#C93B47', fontSize: 13 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
        <div style={card}><div style={lab}>Questions</div><div style={{ fontSize: 26, fontWeight: 700 }}>{nice(tot.q)}</div></div>
        {isAdmin && <div style={card}><div style={lab}>Estimated cost</div><div style={{ fontSize: 26, fontWeight: 700 }}>{money(tot.cost)}</div><div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{tot.q ? money(tot.cost / tot.q) + ' per question' : '\u2014'}</div></div>}
        <div style={card}><div style={lab}>Approved changes</div><div style={{ fontSize: 26, fontWeight: 700 }}>{nice(audit.filter(a => a.ok).length)}</div><div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{audit.filter(a => !a.ok).length} failed</div></div>
        {isAdmin && <div style={card}><div style={lab}>Cache savings</div><div style={{ fontSize: 26, fontWeight: 700 }}>{saved}%</div><div style={{ fontSize: 11.5, color: 'var(--muted)' }}>of input read from cache</div></div>}
      </div>

      {isAdmin && <div style={card}>
        <div style={{ ...lab, marginBottom: 8 }}>AI allowance &mdash; used / monthly limit</div>
        {allowance.length ? allowance.map(x => (
          <div key={x.id} style={{ padding: '6px 0', borderBottom: '0.5px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13, flexWrap: 'wrap' }}>
              <span>{x.name}</span>
              <span style={{ color: TONE[x.tone], fontWeight: x.tone === 'red' || x.tone === 'amber' ? 700 : 400 }}>
                {money(x.used)} / {x.limit == null ? 'no limit' : money(x.limit)}{x.pct != null ? ' \u00b7 ' + Math.round(100 * x.pct) + '%' : ''}{x.tone === 'red' ? ' \u00b7 AI stopped' : ''}
              </span>
            </div>
            {x.limit != null && <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', marginTop: 5, overflow: 'hidden' }}>
              <div style={{ width: Math.min(100, Math.round(100 * x.pct)) + '%', height: '100%', background: TONE[x.tone], borderRadius: 3 }} />
            </div>}
          </div>
        )) : <div style={{ fontSize: 13, color: 'var(--muted)' }}>No AI use and no limits this month.</div>}
        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 8 }}>Set a vessel&rsquo;s limit in Admin &rarr; Vessels &rarr; Manage. At 100% the vessel&rsquo;s AI stops until the 1st of the next month.</div>
      </div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
        {vessel === 'all' && <div style={card}>
          <div style={{ ...lab, marginBottom: 8 }}>By vessel</div>
          {byVessel.length ? byVessel.map(x => (
            <div key={x.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '5px 0', borderBottom: '0.5px solid var(--border)' }}>
              <span>{x.name}</span><span style={{ color: 'var(--muted)' }}>{nice(x.q)} questions{isAdmin ? <> &middot; {money(x.cost)}</> : null}</span>
            </div>
          )) : <div style={{ fontSize: 13, color: 'var(--muted)' }}>No questions this month.</div>}
        </div>}
        <div style={card}>
          <div style={{ ...lab, marginBottom: 8 }}>By user</div>
          {byUser.length ? byUser.map(u => (
            <div key={u.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '5px 0', borderBottom: '0.5px solid var(--border)' }}>
              <span>{u.name}</span><span style={{ color: 'var(--muted)' }}>{nice(u.q)} questions{isAdmin ? <> &middot; {money(u.cost)}</> : null}</span>
            </div>
          )) : <div style={{ fontSize: 13, color: 'var(--muted)' }}>No questions this month.</div>}
        </div>
        <div style={card}>
          <div style={{ ...lab, marginBottom: 8 }}>By day</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 110 }}>
            {days.map(d => <div key={d} title={d + ': ' + byDay[d] + ' questions'} style={{ flex: 1, minWidth: 4, height: Math.max(4, 100 * byDay[d] / maxDay), background: 'var(--gold)', borderRadius: 3, opacity: .85 }} />)}
            {!days.length && <div style={{ fontSize: 13, color: 'var(--muted)' }}>&mdash;</div>}
          </div>
        </div>
      </div>

      <div style={card}>
        <div style={{ ...lab, marginBottom: 8 }}>Audit &mdash; every change approved through the AI</div>
        {audit.length ? audit.map((a, i) => {
          const done = (a.changes && a.changes.done) || []
          return (
            <div key={i} style={{ display: 'flex', gap: 12, fontSize: 13, padding: '7px 0', borderBottom: '0.5px solid var(--border)', alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap', minWidth: 120 }}>{new Date(a.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              <span style={{ minWidth: 110, color: 'var(--muted)' }}>{vessels[a.client_id] || ''}</span>
              <span style={{ minWidth: 130 }}>{who(a.user_id)}</span>
              <span style={{ flex: 1 }}>{a.ok ? (done.length ? done.join(' \u00b7 ') : a.tool.replace(/_/g, ' ')) : <span style={{ color: '#C93B47' }}>{'\u26a0\ufe0f ' + a.tool.replace(/_/g, ' ') + ' failed: ' + (a.error || '')}</span>}</span>
            </div>
          )
        }) : <div style={{ fontSize: 13, color: 'var(--muted)' }}>No AI changes approved this month.</div>}
      </div>
    </div>
  )
}
